/**
 * Demo Data for Executive Dashboard
 * 
 * This file contains rich, impressive demo data used exclusively for the Executive View.
 * The Operational View uses real data from the database.
 * 
 * Purpose: Investor presentations, demos, and showcasing platform capabilities
 */

export interface DemoMetrics {
  monthlyRevenue: number;
  previousMonthRevenue: number;
  revenueGrowth: number;
  totalTransactionsThisMonth: number;
  activeMandates: number;
  averageConfidenceScore: number;
  automationRate: number;
  pendingReviews: number;
  processingEfficiency: number;
  avgTimePerTransaction: number;
}

export interface DemoRevenueData {
  month: string;
  actual: number;
  forecast: number;
  monthName: string;
}

export interface DemoClientProfitability {
  name: string;
  revenue: number;
  cost: number;
  profit: number;
  hours: number;
  profitMargin: number;
  efficiency: number;
}

export interface DemoMandateHealth {
  id: string;
  name: string;
  initials: string;
  status: 'excellent' | 'good' | 'attention' | 'critical';
  confidence: number;
  pending: number;
  type: string;
  lastActivity: string;
}

export interface DemoTeamMember {
  name: string;
  role: string;
  utilization: number;
  accuracy: number;
  tasksCompleted: number;
  avgTimePerTask: number;
}

export interface DemoStrategicAlert {
  type: 'opportunity' | 'warning' | 'info' | 'critical';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  mandates: string[];
  timestamp: string;
  actionable: boolean;
}

/**
 * Executive Dashboard KPI Metrics
 */
export const DEMO_METRICS: DemoMetrics = {
  monthlyRevenue: 184500,
  previousMonthRevenue: 167200,
  revenueGrowth: 10.3,
  totalTransactionsThisMonth: 1247,
  activeMandates: 12,
  averageConfidenceScore: 94.5,
  automationRate: 87.5,
  pendingReviews: 23,
  processingEfficiency: 92.3,
  avgTimePerTransaction: 2.4, // minutes
};

/**
 * 12 Months Revenue History (Actual vs Forecast)
 * Dynamically generated based on current date
 */
export const DEMO_REVENUE_DATA: DemoRevenueData[] = (() => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentYear = now.getFullYear();
  
  const data: DemoRevenueData[] = [];
  
  // Generate 12 months of data (11 months back + current month)
  for (let i = 11; i >= 0; i--) {
    const monthDate = new Date(currentYear, currentMonth - i, 1);
    const monthName = monthDate.toLocaleString('en-US', { month: 'short' });
    const monthString = monthDate.toISOString().slice(0, 7);
    
    // Only the last 3 months (including current) have "actual" values
    // Earlier months show forecast only
    const isActual = i <= 2;
    const baseRevenue = 150000;
    const growthPerMonth = 15000;
    const actual = isActual ? baseRevenue + ((2 - i) * growthPerMonth) : 0;
    const forecast = baseRevenue + ((11 - i) * 8000);
    
    data.push({ 
      month: monthString, 
      monthName, 
      actual, 
      forecast 
    });
  }
  
  return data;
})();

/**
 * Client Profitability Matrix
 */
export const DEMO_CLIENT_PROFITABILITY: DemoClientProfitability[] = [
  {
    name: 'Innovate Zurich AG',
    revenue: 45000,
    cost: 12000,
    profit: 33000,
    hours: 120,
    profitMargin: 73.3,
    efficiency: 275, // CHF per hour
  },
  {
    name: 'Alpine Logistics GmbH',
    revenue: 32000,
    cost: 15000,
    profit: 17000,
    hours: 180,
    profitMargin: 53.1,
    efficiency: 94.4,
  },
  {
    name: 'Swiss Precision SA',
    revenue: 28000,
    cost: 8000,
    profit: 20000,
    hours: 95,
    profitMargin: 71.4,
    efficiency: 210.5,
  },
  {
    name: 'TechStart Basel',
    revenue: 24000,
    cost: 11000,
    profit: 13000,
    hours: 140,
    profitMargin: 54.2,
    efficiency: 92.9,
  },
  {
    name: 'Zürich Retail AG',
    revenue: 18500,
    cost: 9500,
    profit: 9000,
    hours: 110,
    profitMargin: 48.6,
    efficiency: 81.8,
  },
  {
    name: 'Bern Finance Group',
    revenue: 22000,
    cost: 7500,
    profit: 14500,
    hours: 85,
    profitMargin: 65.9,
    efficiency: 170.6,
  },
  {
    name: 'Geneva Consulting SA',
    revenue: 38000,
    cost: 14000,
    profit: 24000,
    hours: 145,
    profitMargin: 63.2,
    efficiency: 165.5,
  },
  {
    name: 'Lucerne Manufacturing',
    revenue: 29000,
    cost: 13000,
    profit: 16000,
    hours: 155,
    profitMargin: 55.2,
    efficiency: 103.2,
  },
];

/**
 * Mandate Health Status
 */
export const DEMO_MANDATE_HEALTH: DemoMandateHealth[] = [
  {
    id: 'MND-001',
    name: 'Innovate Zurich AG',
    initials: 'IZ',
    status: 'excellent',
    confidence: 96,
    pending: 3,
    type: 'full bookkeeping',
    lastActivity: '2 hours ago',
  },
  {
    id: 'MND-002',
    name: 'Alpine Logistics GmbH',
    initials: 'AL',
    status: 'good',
    confidence: 89,
    pending: 12,
    type: 'full bookkeeping',
    lastActivity: '5 hours ago',
  },
  {
    id: 'MND-003',
    name: 'Swiss Precision SA',
    initials: 'SP',
    status: 'excellent',
    confidence: 94,
    pending: 5,
    type: 'full bookkeeping',
    lastActivity: '1 hour ago',
  },
  {
    id: 'MND-004',
    name: 'TechStart Basel',
    initials: 'TB',
    status: 'attention',
    confidence: 78,
    pending: 28,
    type: 'vat only',
    lastActivity: '1 day ago',
  },
  {
    id: 'MND-005',
    name: 'Zürich Retail AG',
    initials: 'ZR',
    status: 'good',
    confidence: 91,
    pending: 8,
    type: 'full bookkeeping',
    lastActivity: '3 hours ago',
  },
  {
    id: 'MND-006',
    name: 'Bern Finance Group',
    initials: 'BF',
    status: 'excellent',
    confidence: 97,
    pending: 2,
    type: 'consulting',
    lastActivity: '30 minutes ago',
  },
  {
    id: 'MND-007',
    name: 'Geneva Consulting SA',
    initials: 'GC',
    status: 'good',
    confidence: 88,
    pending: 15,
    type: 'full bookkeeping',
    lastActivity: '4 hours ago',
  },
  {
    id: 'MND-008',
    name: 'Lucerne Manufacturing',
    initials: 'LM',
    status: 'good',
    confidence: 90,
    pending: 10,
    type: 'full bookkeeping',
    lastActivity: '6 hours ago',
  },
];

/**
 * Team Utilization & Performance
 */
export const DEMO_TEAM_MEMBERS: DemoTeamMember[] = [
  {
    name: 'Sarah Mueller',
    role: 'Senior Accountant',
    utilization: 92,
    accuracy: 98,
    tasksCompleted: 247,
    avgTimePerTask: 2.1,
  },
  {
    name: 'Thomas Weber',
    role: 'Junior Accountant',
    utilization: 85,
    accuracy: 94,
    tasksCompleted: 189,
    avgTimePerTask: 3.2,
  },
  {
    name: 'Lisa Schmidt',
    role: 'VAT Specialist',
    utilization: 78,
    accuracy: 99,
    tasksCompleted: 156,
    avgTimePerTask: 2.8,
  },
  {
    name: 'Marco Bianchi',
    role: 'Accountant',
    utilization: 88,
    accuracy: 96,
    tasksCompleted: 214,
    avgTimePerTask: 2.5,
  },
];

/**
 * Strategic Alerts & Opportunities
 */
export const DEMO_STRATEGIC_ALERTS: DemoStrategicAlert[] = [
  {
    type: 'opportunity',
    priority: 'high',
    title: 'VAT Optimization Potential',
    description: 'Identified CHF 12,500 in potential VAT savings across 3 mandates through improved classification',
    mandates: ['Innovate Zurich AG', 'TechStart Basel', 'Alpine Logistics GmbH'],
    timestamp: '2 hours ago',
    actionable: true,
  },
  {
    type: 'warning',
    priority: 'medium',
    title: 'Approaching Deadline',
    description: 'Q4 2024 VAT declaration due in 5 days for TechStart Basel',
    mandates: ['TechStart Basel'],
    timestamp: '1 day ago',
    actionable: true,
  },
  {
    type: 'opportunity',
    priority: 'medium',
    title: 'Automation Efficiency Gain',
    description: 'AI confidence improved by 8.2% this month through continuous learning. Consider expanding automation rules.',
    mandates: ['Swiss Precision SA', 'Geneva Consulting SA'],
    timestamp: '3 days ago',
    actionable: true,
  },
  {
    type: 'info',
    priority: 'low',
    title: 'New Pattern Detected',
    description: 'AI identified recurring supplier pattern for "SwissPost Logistics" - recommend creating automatic rule',
    mandates: ['Alpine Logistics GmbH'],
    timestamp: '5 days ago',
    actionable: false,
  },
];

/**
 * Helper function to calculate derived metrics
 */
export const getDerivedMetrics = (metrics: DemoMetrics) => {
  const revenueChange = metrics.monthlyRevenue - metrics.previousMonthRevenue;
  const revenueChangePercent = (revenueChange / metrics.previousMonthRevenue) * 100;
  
  return {
    ...metrics,
    revenueChange,
    revenueChangePercent: Math.round(revenueChangePercent * 10) / 10,
    totalRevenueLast3Months: metrics.monthlyRevenue + metrics.previousMonthRevenue + (metrics.previousMonthRevenue * 0.92),
    avgRevenuePerMandate: Math.round(metrics.monthlyRevenue / metrics.activeMandates),
    avgConfidenceLevel: metrics.averageConfidenceScore >= 90 ? 'Excellent' : 
                        metrics.averageConfidenceScore >= 80 ? 'Good' : 
                        metrics.averageConfidenceScore >= 70 ? 'Fair' : 'Needs Attention',
  };
};

/**
 * Helper to format Swiss currency for display
 */
export const formatDemoCurrency = (amount: number, compact = false): string => {
  if (compact && amount >= 1000) {
    return `CHF ${(amount / 1000).toFixed(1)}k`;
  }
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Helper to get status color
 */
export const getStatusColor = (status: DemoMandateHealth['status']) => {
  switch (status) {
    case 'excellent':
      return 'text-green-600 bg-green-50';
    case 'good':
      return 'text-blue-600 bg-blue-50';
    case 'attention':
      return 'text-yellow-600 bg-yellow-50';
    case 'critical':
      return 'text-red-600 bg-red-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};
